import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  Scale, Gavel, ShieldAlert, ArrowRight, FileText, Quote, Users2,
  AlertTriangle, BookOpen,
} from "lucide-react";

const ICONS = {
  "first-amendment": Scale,
  "criminal-defense": Gavel,
  "section-1983": ShieldAlert,
  "prosecutor-misconduct": AlertTriangle,
  "appellate": BookOpen,
  "official-misconduct": ShieldAlert,
};

export default function Defenders() {
  const [founder, setFounder] = useState(null);
  const [principles, setPrinciples] = useState([]);
  const [areas, setAreas] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/defenders/founder").then(r => setFounder(r.data));
    api.get("/defenders/principles").then(r => setPrinciples(r.data));
    api.get("/defenders/practice-areas").then(r => setAreas(r.data));
    api.get("/defenders/authorities").then(r => setAuthorities(r.data));
  }, []);

  useEffect(() => {
    const params = filter ? { practice_area: filter } : {};
    api.get("/defenders/lawyers", { params }).then(r => setLawyers(r.data));
  }, [filter]);

  return (
    <>
      {/* HERO */}
      <section
        className="text-white relative"
        style={{
          background: `linear-gradient(180deg, rgba(11,37,69,0.92) 0%, rgba(11,37,69,0.98) 100%), url('https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=2000&q=70') center/cover no-repeat`,
        }}
        data-testid="defenders-hero"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ AVAILABLE DEFENDERS · 1A · CRIMINAL DEFENSE · §1983
          </div>
          <h1 className="display-black text-5xl lg:text-7xl uppercase mt-6 leading-[0.92] tracking-tighter max-w-5xl">
            We don't take pleas.<br />
            We file motions.<br />
            <span className="text-[#FACC15]">We sue prosecutors.</span>
          </h1>
          <p className="text-lg lg:text-xl text-white/85 mt-7 max-w-3xl leading-relaxed">
            A network of First Amendment lawyers, federal criminal defenders, and §1983 civil-rights litigators who denounce the modern plea-bargain mill — and fight prosecutor and official misconduct at every level.
          </p>

          <div className="mt-10 inline-flex items-center gap-3 mono text-[12px] tracking-[0.22em] uppercase text-[#FACC15] border-l-2 border-[#FACC15] pl-4">
            <Quote className="w-4 h-4" />
            Thompson v. Clark, 596 U.S. 36 (2022) — the door we walk through.
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/defenders/intake" className="btn-signal" data-testid="defenders-hire-cta">Hire a Fighter <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/defenders/join" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }} data-testid="defenders-join-cta">Join the Network</Link>
            <a href="#founder" className="btn-ghost" style={{ color: "#FACC15", borderColor: "#FACC15" }} data-testid="defenders-founder-cta">Read the Founder's Story</a>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      {founder && (
        <section id="founder" className="bg-white py-20 lg:py-28 border-b border-gray-300" data-testid="defenders-founder">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// The Founder</div>
              <h2 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter">{founder.name}</h2>
              <div className="mono text-[12px] tracking-wider uppercase text-gray-600 mt-2">{founder.title} · Age {founder.age}</div>
              <blockquote className="mt-8 border-l-4 border-[#D97706] pl-5 text-xl text-[#064E3B] italic">
                "{founder.creed}"
              </blockquote>
              <div className="mt-8 grid grid-cols-2 gap-px bg-gray-300 border border-gray-300">
                <div className="bg-white p-4 text-center">
                  <div className="display-black text-3xl text-[#064E3B]">{founder.civil_wins}</div>
                  <div className="mono text-[10px] tracking-wider text-gray-500 uppercase mt-1">Federal Civil Wins</div>
                </div>
                <div className="bg-white p-4 text-center">
                  <div className="display-black text-2xl text-[#D97706]">{founder.time_served}</div>
                  <div className="mono text-[10px] tracking-wider text-gray-500 uppercase mt-1">Time Served</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <p className="text-lg text-gray-800 leading-relaxed">{founder.bio_short}</p>

              <div className="mt-8 border border-gray-300 p-6 bg-gray-50">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Case of Record</div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Citation</div>
                    <div className="font-bold text-[#064E3B]">{founder.case_citation}</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Trial Court Case</div>
                    <div className="font-bold text-[#064E3B]">{founder.trial_court_case}</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Trial Judge</div>
                    <div className="font-bold text-[#064E3B]">{founder.trial_judge}</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Ex-USAUSA (Adverse)</div>
                    <div className="font-bold text-[#064E3B]">{founder.x_usada}</div>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Milestones</div>
                <div className="mt-4 border-l-2 border-[#064E3B] space-y-5 pl-6">
                  {founder.milestones.map((m, i) => (
                    <div key={`milestone-${i}`} className="relative" data-testid={`milestone-${i}`}>
                      <div className="absolute -left-[31px] top-1 w-4 h-4 bg-[#D97706] border-2 border-white" />
                      <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">{m.year}</div>
                      <div className="text-base text-gray-800 mt-1 leading-relaxed">{m.event}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 border border-[#064E3B] bg-[#064E3B] text-white p-6">
                <FileText className="w-5 h-5 text-[#FACC15]" />
                <div className="display-black text-lg uppercase mt-3">First-Week Suit. Halfway-House Filing. Treasury Settlement.</div>
                <p className="text-sm text-white/85 mt-3">
                  The week he was released into the halfway house, the Founder filed his first civil action against the government. The case settled — the United States Treasury cut a check pursuant to a judgement order. That settlement is the spine of this network.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section className="py-20 lg:py-28 bg-gray-50" data-testid="defenders-principles">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// The Six Principles</div>
          <h2 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter max-w-3xl">
            What we believe. What we file.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-300 border border-gray-300">
            {principles.map(p => (
              <div key={p.n} className="bento-card p-7" data-testid={`principle-${p.n}`}>
                <div className="display-black text-4xl text-[#FACC15] bg-[#064E3B] w-14 h-14 flex items-center justify-center">{p.n}</div>
                <h3 className="display-black text-xl uppercase text-[#064E3B] mt-5">{p.title}</h3>
                <p className="text-sm text-gray-800 mt-3 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS + DIRECTORY */}
      <section className="py-20 lg:py-28" data-testid="defenders-directory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Member Directory</div>
          <h2 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter">Fighters. Vetted.</h2>
          <p className="text-gray-700 mt-3 max-w-2xl">
            Every member is screened on the no-plea creed and verified §1983 trial record. Membership is reviewed by the Founder.
          </p>

          {/* Practice area filter */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="defenders-area-filter">
            <button
              onClick={() => setFilter("")}
              className={`mono text-[11px] tracking-[0.22em] uppercase px-4 py-2 border ${filter === "" ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white text-[#064E3B] border-gray-300 hover:border-[#064E3B]"}`}
              data-testid="area-filter-all"
            >
              All
            </button>
            {areas.map(a => (
              <button
                key={a.slug}
                onClick={() => setFilter(a.slug)}
                className={`mono text-[11px] tracking-[0.22em] uppercase px-4 py-2 border ${filter === a.slug ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white text-[#064E3B] border-gray-300 hover:border-[#064E3B]"}`}
                data-testid={`area-filter-${a.slug}`}
              >
                {a.name}
              </button>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-300 border border-gray-300">
            {lawyers.map(l => (
              <article key={l.id} className="bento-card p-7" data-testid={`lawyer-${l.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="display-black text-xl uppercase text-[#064E3B] leading-tight">{l.name}</h3>
                    <div className="mono text-[11px] tracking-wider text-gray-500 uppercase mt-1">{l.firm}</div>
                    <div className="mono text-[11px] tracking-wider text-[#D97706] uppercase mt-1">{l.jurisdiction}</div>
                  </div>
                  {l.founder_endorsed && (
                    <span className="mono text-[9px] tracking-[0.22em] uppercase px-2 py-1 bg-[#FACC15] text-[#064E3B] font-bold whitespace-nowrap">★ Founder Endorsed</span>
                  )}
                </div>
                <blockquote className="border-l-4 border-[#D97706] pl-4 mt-5 text-sm italic text-gray-800">
                  "{l.philosophy}"
                </blockquote>
                <div className="mt-4 text-sm text-gray-800">
                  <span className="font-bold text-[#064E3B]">Specialty:</span> {l.specialty}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {l.practice_areas.map(pa => (
                    <span key={pa} className="mono text-[10px] tracking-wider uppercase px-2 py-1 border border-gray-300 text-gray-700">
                      {pa.replace(/-/g, " ")}
                    </span>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4">
                  <div>
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Bar Years</div>
                    <div className="display-black text-xl text-[#064E3B]">{l.bar_years}</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Trials Tried</div>
                    <div className="display-black text-xl text-[#D97706]">{l.trials_tried}</div>
                  </div>
                </div>
              </article>
            ))}
            {lawyers.length === 0 && <div className="bg-white p-10 md:col-span-2 text-center text-gray-500">No members in this practice area yet.</div>}
          </div>
        </div>
      </section>

      {/* AUTHORITIES */}
      <section className="py-20 bg-[#064E3B] text-white" data-testid="defenders-authorities">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#FACC15]">// Key Authorities</div>
          <h2 className="display-black text-3xl lg:text-4xl uppercase mt-3">The case law we live by.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/15">
            {authorities.map(a => (
              <div key={a.cite} className="bg-[#064E3B] p-6" data-testid={`authority-${a.cite.split(",")[0].replace(/[\s.]/g, "")}`}>
                <div className="display-black text-base uppercase text-[#FACC15] leading-tight">{a.cite}</div>
                <p className="text-sm text-white/85 mt-3 leading-relaxed">{a.summary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-28">
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-300 border border-gray-300">
          <Link to="/defenders/motions" className="bento-card p-10" data-testid="cta-motion-of-week">
            <BookOpen className="w-8 h-8 text-[#D97706]" />
            <h3 className="display-black text-3xl uppercase text-[#064E3B] mt-5">Motion of the Week</h3>
            <p className="text-gray-800 mt-3">Real motions. Detailed teardowns. Free. Weekly. The legal education the plea-mill bar will not give you.</p>
            <div className="mono text-[11px] tracking-wider uppercase text-[#D97706] mt-6 flex items-center gap-2">
              Read Archive <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
          <Link to="/defenders/intake" className="bento-card p-10" data-testid="cta-hire-fighter">
            <Users2 className="w-8 h-8 text-[#D97706]" />
            <h3 className="display-black text-3xl uppercase text-[#064E3B] mt-5">Need a fighter?</h3>
            <p className="text-gray-800 mt-3">Active case · federal investigation · §1983 claim · 1A retaliation. We route you to the right member within 24 hours.</p>
            <div className="mono text-[11px] tracking-wider uppercase text-[#064E3B] mt-6 flex items-center gap-2">
              Client Intake <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        </div>
      </section>
    </>
  );
}
